import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Form,
  Image,
  message,
  Modal,
  Tabs,
  Tag,
  Typography,
  Drawer,
} from "antd";
import ReassessmentForm from "./ReassessmentForm";
import { useLocation } from "react-router-dom";
import NutritionistClinicalEvaluationForm from "../../../IpEMR/InspectionForms/ClinicalEvaluation/NutritionistClinicalEvaluationForm";
import NutritionistGoalsForm from "../../../IpEMR/InspectionForms/Goals/NutritionistGoalsForm";
import NutritionistDietAdvice from "../../../IpEMR/InspectionForms/DietAdvice/NutritionistDietAdvice";
import { LeftOutlined } from "@ant-design/icons";
import bedImg from "../../../../assets/images/patientIcons/bed.svg";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../../IpEMR/Options/historyOptions";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
} from "../formsKeys";
import dayjs from "dayjs";
import {
  submitNutritionistReassessmentFormApi,
  updateNutritionistReassessmentFormApi,
} from "../../../../services/forms.services";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../../IpEMR/Doctor/InitialInspection/InitialInspectionHeader.css";
import "../../../IpEMR/Doctor/DailyReview/DailyReview.css";
import "../FormsIp.css";

const ReassessmentTabs = ({
  patientData,
  openModal,
  setOpenModal,
  details,
  pressureSorePoints,
  refreshPage,
  reviewList,
  data,
  formData,
  setActiveSegment,
  activeSegment,
}) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const formBodyRef = useRef(null);
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);
  const [assessmentForm] = Form.useForm();
  const [clinicalEvaluationForm] = Form.useForm();
  const [goalsForm] = Form.useForm();
  const [dietForm] = Form.useForm();
  const params = Object.fromEntries(new URLSearchParams(location?.search));

  const tabItems = useMemo(
    () => [
      { key: "1", label: "Assessment" },
      {
        key: "2",
        label: isMobile ? "Evaluation" : "Clinical Evaluation",
      },
      { key: "3", label: "Goals" },
      { key: "4", label: isMobile ? "Diet" : "Diet Advice" },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const onAssessmentSubmit = (values) => {
    const payload = {
      patient: details?.data?.patient,
      diagnosis: details?.data?.diagnosis,
      medications: details?.data?.medications,
      vitals: details?.data?.vitals,
      nebulization: details?.data?.nebulization,
      past_medical_history: details?.data?.present_illness
        ? getPastMedicalHistoryFormSummary(
            pastMedicalOptions,
            details?.data?.present_illness
          )
        : "",
      known_allergies: details?.data?.present_illness
        ? getLabelTagsWithFormDescription(
            knownAllergiesOptions,
            details?.data?.present_illness
          )
        : "",
      past_surgical_history: details?.data?.present_illness?.surgical_history
        ? details?.data?.present_illness?.surgical_history
        : null,
      past_surgical_history_details: details?.data?.present_illness
        ?.surgical_history_details
        ? details?.data?.present_illness?.surgical_history_details
        : null,
      ...values,

      clinical_progress: {
        ...values?.clinical_progress,
        date: values?.clinical_progress?.date
          ? dayjs(values.clinical_progress.date).format("YYYY-MM-DD")
          : null,
      },
      pressure_sore: pressureSorePoints,
    };
    handleSubmit(
      {
        data: payload,
        admission_id: params?.patient,
      },
      "Assessment"
    );
  };

  const onClinicalEvaluationSubmit = (values) => {
    handleSubmit(
      {
        clinical_evaluation: values,
        admission_id: params?.patient,
      },
      "Clinical Evaluation"
    );
  };

  const onGoalsSubmit = (values) => {
    handleSubmit(
      {
        goals: values,
        admission_id: params?.patient,
      },
      "Goals"
    );
  };

  const onDietSubmit = (values) => {
    handleSubmit(
      {
        diet_advice: values,
        admission_id: params?.patient,
      },
      "Diet Advice"
    );
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      switch (activeTab) {
        case "1":
          assessmentForm.validateFields().then((values) => {
            setIsDirty(false);
            onAssessmentSubmit(values);
            setActiveTab(key);
          });
          break;
        case "2":
          clinicalEvaluationForm.validateFields().then((values) => {
            setIsDirty(false);
            onClinicalEvaluationSubmit(values);
            setActiveTab(key);
          });
          break;
        case "3":
          goalsForm.validateFields().then((values) => {
            setIsDirty(false);
            onGoalsSubmit(values);
            setActiveTab(key);
          });
          break;
        case "4":
          dietForm.validateFields().then((values) => {
            setIsDirty(false);
            onDietSubmit(values);
            setActiveTab(key);
          });
          break;
        default:
          setActiveTab(key);
      }
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return assessmentForm.submit();
      case "2":
        return clinicalEvaluationForm.submit();
      case "3":
        return goalsForm.submit();
      case "4":
        return dietForm.submit();
      default:
        return "";
    }
  };

  const handleSubmit = async (values, flag) => {
    try {
      if (activeSegment) {
        await updateNutritionistReassessmentFormApi(activeSegment, {
          ...values,
          diet_type:
            values?.diet_advice?.diet_details?.diet ??
            formData?.diet_advice?.diet_details?.diet ??
            null,
        });
      } else {
        await submitNutritionistReassessmentFormApi({
          ...values,
          diet_type:
            values?.diet_advice?.diet_details?.diet ??
            formData?.diet_advice?.diet_details?.diet ??
            null,
        });
      }

      message.success(
        activeSegment
          ? `${flag} Saved successfully`
          : `${flag} Submitted successfully`
      );
      if (buttonPressed) {
        if (activeTab === "4") {
          setOpenModal(false);
        } else {
          const nextTab = (parseInt(activeTab, 10) + 1).toString();
          setActiveTab(nextTab);
        }
        setButtonPressed(false);
      }
      setIsDirty(false);
      refreshPage();
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleClose = () => {
    setOpenModal(false);
    setActiveSegment(reviewList[0]?.id);
  };

  const patientName = `${patientData?.admission?.patient?.first_name || ""} ${
    patientData?.admission?.patient?.last_name || ""
  }`.trim();

  return (
    <Drawer
      open={openModal}
      onClose={handleClose}
      width="100%"
      closeIcon={null}
      keyboard={false}
      styles={{
        body: {
          overflow: "hidden",
          paddingTop: 0,
          paddingBottom: 0,
          paddingInline: isMobile ? 12 : 24,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
      title={
        <div className="ia-drawer-header">
          <div className="ia-drawer-header__main">
            <LeftOutlined
              className="ia-drawer-header__back"
              onClick={() => {
                Modal.confirm({
                  title: "Confirmation",
                  content: "Are you sure you want to close?",
                  onOk: handleClose,
                });
              }}
            />
            <div className="ia-drawer-header__identity">
              <div className="ia-drawer-header__name-row">
                <Typography.Text
                  className="ia-drawer-header__name"
                  ellipsis={{ tooltip: patientName }}
                >
                  {patientName}
                </Typography.Text>
                {patientData?.admission?.bed?.bed_no && (
                  <Tag color="orange" className="ia-drawer-header__bed">
                    <Image src={bedImg} preview={false} width={12} />{" "}
                    {patientData?.admission?.bed?.bed_no}
                  </Tag>
                )}
              </div>
              <div className="ia-drawer-header__meta">
                <Tag className="ia-drawer-header__tag">
                  {patientData?.admission?.patient?.age_by_dob ||
                    patientData?.admission?.patient?.age}{" "}
                  {patientData?.admission?.patient?.gender}
                </Tag>
                <Tag className="ia-drawer-header__tag">
                  IP ID: {patientData?.admission?.ip_id}
                </Tag>
              </div>
            </div>
          </div>
        </div>
      }
      footer={null}
    >
      <div className="daily-review-form-shell">
        <div className="initial-inspection-form-wrapper">
          <div className="ia-form-tabs">
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={handleTabChange}
            />
          </div>
          <div className="ia-form-panel">
            <div className="ia-form-body" ref={formBodyRef}>
              {activeTab === "1" && (
                <Form
                  form={assessmentForm}
                  layout="vertical"
                  onFinish={onAssessmentSubmit}
                  onValuesChange={onFormValuesChange}
                  className="ip-forms nutrition-reassessment-form forms-ip-shell"
                >
                  <ReassessmentForm
                    patientData={patientData}
                    formData={data}
                    form={assessmentForm}
                  />
                </Form>
              )}
              {activeTab === "2" && (
                <Form
                  form={clinicalEvaluationForm}
                  layout="vertical"
                  onFinish={onClinicalEvaluationSubmit}
                  onValuesChange={onFormValuesChange}
                  className="ip-forms nutrition-reassessment-form forms-ip-shell"
                >
                  <NutritionistClinicalEvaluationForm
                    form={clinicalEvaluationForm}
                    formData={formData}
                  />
                </Form>
              )}
              {activeTab === "3" && (
                <Form
                  form={goalsForm}
                  layout="vertical"
                  onFinish={onGoalsSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData?.goals}
                  className="ip-forms nutrition-reassessment-form forms-ip-shell"
                >
                  <NutritionistGoalsForm data={formData} form={goalsForm} />
                </Form>
              )}
              {activeTab === "4" && (
                <Form
                  form={dietForm}
                  layout="vertical"
                  onFinish={onDietSubmit}
                  onValuesChange={onFormValuesChange}
                  className="ip-forms nutrition-reassessment-form forms-ip-shell"
                >
                  <NutritionistDietAdvice form={dietForm} formData={formData} />
                </Form>
              )}
            </div>
            <div className="ia-form-footer">
              <Button
                onClick={() => {
                  getFormKey();
                  setButtonPressed(true);
                }}
                type="primary"
                className={btnStyles.mdBtn}
                style={{ fontWeight: 500 }}
              >
                {activeTab === "4" ? "Done" : "Save & Proceed"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ReassessmentTabs;
